/**
 * The one "Which plant is this photo of?" question (B160): asked from every door that says a photo was filed (the Photos page's
 * "Filed by Grow" strip, the toast right after adding a photo, the plant card's line) and answered in one sheet
 * (MovePhotoSheet, mounted once by MovePhotoHost in the layout), so the toast's Move still works after the sheet it was said from
 * has closed.
 *
 * askMovePhoto opens the sheet for a photo; closeMovePhoto closes it. The move itself is the server's
 * (PUT /photos/:plantObjId/:photoId/plant); this keeps only which photo the sheet is open on.
 *
 * Where: FiledStrip, sayFiled (the toast), PlantCardPhoto; MovePhotoHost draws it.
 *
 * @module movePhoto
 */

/** The photo(s) the sheet is open on: where they are now. */
export interface MovePhotoAsk {
  /** The plant the photos are on now. */
  plantObjId: number;
  /** The photos to move (a photo of one plant is one). */
  photoIds: number[];
}

/** The question now open, or null. */
let current: MovePhotoAsk | null = null;
/** Who draws the sheet. */
const listeners = new Set<() => void>();

/** Tells the host the question changed. */
function changed(): void {
  for (const listener of listeners) listener();
}

/**
 * Opens the sheet on these photos. A question asked while another is open replaces it.
 *
 * @param ask - Where the photos are now.
 */
export function askMovePhoto(ask: MovePhotoAsk): void {
  current = ask;
  changed();
}

/** Closes the sheet. */
export function closeMovePhoto(): void {
  current = null;
  changed();
}

/**
 * The open question, for the host.
 *
 * @returns The photos the sheet is open on, or null when closed.
 */
export function openMovePhoto(): MovePhotoAsk | null {
  return current;
}

/**
 * Tells the host when the question changes.
 *
 * @param listener - Called on every change.
 * @returns A function that stops telling it.
 */
export function subscribeMovePhoto(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
